import { inject, Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { type IGeoCoordinates } from '@principle-theorem/principle-core/interfaces';
import { Region, shareReplayCold, snapshot } from '@principle-theorem/shared';
import { type Observable, from, of } from 'rxjs';
import { map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { OrganisationService } from '../organisation.service';
import { type IGoogleMapsConfig, MAPS_CONFIG } from './config';

const mapsRegionCodes: Record<Region, string> = {
  [Region.Australia]: 'au',
  [Region.NewZealand]: 'nz',
};

@Injectable()
export class GeocodeService {
  private _config: IGoogleMapsConfig = inject(MAPS_CONFIG);
  private _organisation = inject(OrganisationService);
  private _geocoder: google.maps.Geocoder;
  private _mapsLoaded$ = this._loadMaps$().pipe(shareReplayCold());

  async load(): Promise<void> {
    await snapshot(this._ensureMapsLoaded());
  }

  mapsLoaded$(): Observable<boolean> {
    return this._ensureMapsLoaded().pipe(startWith(false));
  }

  geocodeAddress$(practice: string): Observable<IGeoCoordinates | undefined> {
    return this._ensureMapsLoaded().pipe(
      switchMap(() => this._parseAddress(practice))
    );
  }

  geocodeFromAddress$(
    address: string
  ): Observable<google.maps.GeocoderAddressComponent[]> {
    return this._ensureMapsLoaded().pipe(
      switchMap(() => this._getAddress(address))
    );
  }

  geocodeFromCoordinates$(
    coordinates: IGeoCoordinates
  ): Observable<google.maps.GeocoderAddressComponent[]> {
    return this._ensureMapsLoaded().pipe(
      switchMap(() => this._getLocation(coordinates))
    );
  }

  private _initGeocoder(): void {
    this._geocoder = new google.maps.Geocoder();
  }

  private _ensureMapsLoaded(): Observable<boolean> {
    if (this._geocoder) {
      return of(true);
    }
    return this._mapsLoaded$;
  }

  /**
   * Google Maps can only be bootstrapped once per page, so the region bias is
   * taken from the workspace at the time of the first load. js-api-loader
   * throws when asked to load again with other options, so a service created
   * after a workspace switch reuses the Maps already on the page.
   */
  private _loadMaps$(): Observable<boolean> {
    return this._mapsRegion$().pipe(
      switchMap((region) => from(this._bootstrapMaps(region))),
      tap(() => this._initGeocoder()),
      map(() => true)
    );
  }

  private async _bootstrapMaps(region: string): Promise<void> {
    // The Google Charts loader that index.html adds to every page also defines
    // window.google, so only Maps itself counts as already loaded.
    if (typeof google !== 'undefined' && google.maps !== undefined) {
      return;
    }
    await this._createLoader(region).load();
  }

  private _mapsRegion$(): Observable<string> {
    return this._organisation.region$.pipe(
      take(1),
      map((region) => (region ? mapsRegionCodes[region] : this._config.region))
    );
  }

  private _createLoader(region: string): Loader {
    return new Loader({
      apiKey: this._config.apiKey,
      language: this._config.language,
      region,
      libraries: ['places'],
    });
  }

  private async _getAddress(
    address: string
  ): Promise<google.maps.GeocoderAddressComponent[]> {
    const results = await this._geocoder.geocode({ address });
    return results.results[0].address_components;
  }

  private async _getLocation(
    coordinates: IGeoCoordinates
  ): Promise<google.maps.GeocoderAddressComponent[]> {
    const results = await this._geocoder.geocode({
      location: {
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      },
    });
    return results.results[0].address_components;
  }

  private async _parseAddress(
    practice: string
  ): Promise<IGeoCoordinates | undefined> {
    const results = await this._geocoder.geocode({ address: practice });
    const firstResult = results.results[0];
    if (!firstResult) {
      return;
    }
    return {
      latitude: firstResult.geometry.location.lat(),
      longitude: firstResult.geometry.location.lng(),
    };
  }
}
